export type { 
  Role, 
  RoleWithDetails, 
  Permission, 
  GroupedPermissions, 
  RoleUsersPageParams, 
  RoleBreakdownProps, 
  RoleInfo 
} from './types/role.types';

import { Role } from './types/role.types';

export interface Department {
  id: string;
  name: string;
  semestersPerYear?: number;
  maxYearLevels?: number;
}

/**
 * Represents an audit log entry from the system.
 * Used for tracking changes to entities like FEE_STRUCTURE, USER, etc.
 */
export interface AuditLog {
  id: string;
  timestamp: string;
  actor: { fullName: string; email?: string };
  action: string;
  entity: string;
  entityId: string;
  beforeValue: Record<string, unknown> | null;
  afterValue: Record<string, unknown>;
}


export interface DepartmentWithUsage extends Department {
  students: number;
  staff: number;
  feeStructures: number;
  isDeletable: boolean;
}

export type AccountStatus = string;
export type UserRole = string;

export interface User {
  id: string;
  userId?: string;
  username: string;
  fullName: string;
  email: string;
  role: string; // The user's primary role key, e.g., 'SUPER_ADMIN', 'STUDENT'
  departmentId?: string;
  department?: string;
  yearLevel?: string;
  semester?: string;
  status: AccountStatus;
  universityId?: string;
  permissions: string[]; // The new source of truth for authorization
  defaultPath: string; // The user's default dashboard path
  dateCreated: string;
  lastLogin?: string;
  phoneNumber?: string; // Added
  isTwoFactorEnabled?: boolean; // Added for 2FA
  hasActiveFullScholarship?: boolean; // Added
  studentId?: string; // Added for student record lookups
  // Admin specific notification preferences
  notifyOnNewFeedback?: boolean; // Added
  notifyOnCriticalError?: boolean; // Added
  financePermissions?: string[]; // Added for finance staff
  // Identity Management
  baseUsername?: string;
  discriminator?: string;
  usernameGenerationMethod?: string;
  avatar?: string; // Added for profile picture display
  preferences?: {
    themeMode: 'light' | 'dark' | 'system';
    uiSettings: Record<string, unknown>;
  };
}

export interface AuthenticatedUser extends User {
  token: string;
}

export interface AuthContextType {
  user: AuthenticatedUser | null;
  login: (userData: AuthenticatedUser) => void;
  logout: () => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  /**
   * Authenticated API client wrapper.
   * Uses LegacyApiClientOptions for compatibility with apiClient.
   * Supports `data` for JSON body and `params` for query strings.
   */
  fetchWithAuth: <T = unknown>(
    url: string,
    options?: import('./utils/apiClient').LegacyApiClientOptions
  ) => Promise<T>;
  updateUser: (userData: AuthenticatedUser | User) => void;
}

export interface SidebarLink {
  name: string;
  path?: string;
  icon?: React.ReactElement;
  subLinks?: SidebarLink[];
  badgeCount?: number;
}

export type PaymentStatus = string;

export interface PaymentRecord {
  id: string;
  studentId: string;
  studentName: string;
  amount: number; // Amount Paid
  date: string; // Payment Date
  gatewayTransactionId?: string;
  status: PaymentStatus; // Current verification status by staff (e.g., Verified, Rejected, PendingVerification)
  description?: string; // For student payment history, e.g., "Semester I Fee"

  // New/Enhanced fields for Payment Verification Module
  expectedFeeAmount?: number;
  paymentGateway?: 'CHAPA' | 'TELEBIRR' | 'MANUAL' | 'OTHER';
  systemStatus?: string;
  assignedTo?: Pick<User, 'id' | 'fullName'>;
  verificationMethod?: string;
  financeStaffNotes?: string;
  uploadedProofUrl?: string;
  manualPaymentRef?: string;
  studentPaymentPhoneNumber?: string;
  gatewayResponse?: string;
  studentDepartment?: string; // Added for filtering convenience
  studentYearLevel?: string; // Added for context
  student?: {
    universityId: string;
  };
}

export interface UserFilters {
  searchTerm: string;
  roles: string[];
  statuses: AccountStatus[];
  departmentId: string;
  yearLevels: string[];
  semesters: string[];
  dateCreatedFrom: string;
  dateCreatedTo: string;
  lastLoginFrom: string;
  lastLoginTo: string;
}

export type ScholarshipType = string;
export type ScholarshipBenefitType = string;
export type ScholarshipApplicationStatus = string;

export interface ApplicationDocument {
  id: string;
  name: string;
  description: string;
  fileUrl?: string;
  file?: File;
}

export interface ApplicationHistoryLog {
  timestamp: string;
  actor: string;
  action: string;
  details?: string;
}

export interface ScholarshipApplication {
  id: string;
  // Student Info
  studentId: string;
  studentName: string;
  studentDepartment?: string;
  studentYearLevel?: string;
  // Scholarship Info
  scholarshipName: string;
  scholarshipType: ScholarshipType;
  scholarshipDescription: string;
  // Benefit Info
  benefitType: ScholarshipBenefitType;
  benefitValue: number;
  // Term Info
  academicYear: string;
  semester: string;
  // Meta
  applicationDate: string;
  status: ScholarshipApplicationStatus;
  documents: ApplicationDocument[];
  historyLog?: ApplicationHistoryLog[];
  // Review fields
  financeComments?: string;
  financeReviewDate?: string;
  adminComments?: string;
  adminReviewDate?: string;
  assignedFinanceStaffId?: string;
}

// System Configuration Types (existing)
export interface GeneralSettings {
  universityName: string;
  systemName: string;
  systemContactEmail: string;
  systemContactPhone?: string;
  currentAcademicYear: string;
  currentSemester: string;
  defaultCurrency: string;
  dateFormat: string;
  systemTimezone: string;
  logoUrl?: string;
}
export interface EmailConfiguration {
  defaultSenderEmail: string;
  smtpHost?: string;
  smtpPort?: number;
  smtpUsername?: string;
  smtpPassword?: string;
  smtpEncryption?: 'SSL' | 'TLS' | 'NONE';
  testStatus?: 'idle' | 'passed' | 'failed';
}
export interface SmsConfiguration {
  provider?: string;
  apiKey?: string;
  senderId?: string;
  apiUrl?: string;
  httpMethod?: 'GET' | 'POST';
  headers?: Record<string, string>;
  payloadType?: 'JSON' | 'FORM';
  payloadTemplate?: string;
  successPattern?: string;
  userAgent?: string;
  smsCharLimit?: number;
  welcomeSmsEnabled?: boolean;
  testStatus?: 'idle' | 'passed' | 'failed';
  webhookEnabled?: boolean;
  webhookSecret?: string;
  webhookSignatureHeader?: string;
  webhookMessageIdField?: string;
  webhookStatusField?: string;
  webhookUrl?: string;
  responseIdPath?: string;
  webhookSuccessStatuses?: string[];
}
export interface NotificationTemplate {
  id: string;
  templateKey: string;
  name: string;
  description: string;
  channels: ('EMAIL' | 'SMS' | 'IN_APP' | 'PUSH')[];
  enabledChannels?: ('EMAIL' | 'SMS' | 'IN_APP' | 'PUSH')[];
  status: 'ENABLED' | 'DISABLED';
  emailSubject?: string;
  emailBody?: string;
  smsContent?: string;
  inAppContent?: string;
  placeholders?: string[];
  audience?: string;
  category?: string;
}
export interface NotificationSettings {
  emailConfig: EmailConfiguration;
  smsConfig: SmsConfiguration;
  templates: NotificationTemplate[];
}

export type LateFeeCalculationMethod = string;
export type LateFeeFrequency = string;

export interface LateFeePolicy {
  enableAutomaticLateFees: boolean;
  gracePeriodDays: number;
  calculationMethod: LateFeeCalculationMethod;
  feeValue: number;
  frequency: LateFeeFrequency;
}
export interface RegistrationBlockPolicy {
  enableAutomaticBlocking: boolean;
  blockAfterMissedInstallments: number;
  blockIfOutstandingBalanceExceeds: number;
  notificationMessage: string;
}
export interface PaymentReminderPolicy {
  enablePaymentReminders: boolean;
  reminderDaysBeforeDue: string;
  reminderWorkerSchedule: string;
  reminderBatchSize: number;
  enableInstallmentReminders: boolean;
}
export interface FinancialPolicies {
  lateFeePolicy: LateFeePolicy;
  registrationBlockPolicy: RegistrationBlockPolicy;
  paymentReminderPolicy: PaymentReminderPolicy;
}
export interface SecuritySettings {
  sessionTimeoutMinutes: number;
  passwordRequiresUppercase: boolean;
  passwordRequiresLowercase: boolean;
  passwordRequiresNumber: boolean;
  passwordRequiresSymbol: boolean;
  minPasswordLength: number;
  accountLockoutThreshold: number;
  accountLockoutDurationMinutes: number;
  enableStaffAdmin2FA: boolean;
  recaptchaScoreThreshold: number;
}
export interface RateLimitConfig {
  apiMax: number;
  apiWindowMs: number;
  loginMax: number;
  loginWindowMs: number;
  authMax: number;
  authWindowMs: number;
  passwordVerifyMax: number;
  passwordVerifyWindowMs: number;
  configUpdateMax: number;
  configUpdateWindowMs: number;
  smsMax: number;
  smsWindowMs: number;
  observabilityMax: number;
  observabilityWindowMs: number;
  version?: number;
}
export interface MaintenanceSettings {
  maintenanceModeEnabled: boolean;
  maintenanceMessage: string;
  expectedDowntime?: string;
}

// ===== System Health Types =====

/**
 * Represents the status of a single system component (e.g., Database, Redis).
 */
export interface HealthIndicatorStatus {
  status: 'UP' | 'DOWN';
  message?: string;
  details?: Record<string, unknown>;
}

/**
 * Represents the overall health status of the system.
 */
export interface SystemHealthStatus {
  isHealthy: boolean;
  database: HealthIndicatorStatus;
  redis: HealthIndicatorStatus;
  timestamp: string;
}

export interface SystemConfig {
  general: Partial<GeneralSettings>;
  financial: Partial<FinancialPolicies>;
  security: Partial<SecuritySettings>;
  maintenance: Partial<MaintenanceSettings>;
  notifications: Partial<NotificationSettings>;
}

// System Reports & Logs Types (existing)
export type LogSeverity = string;
export type SecurityEventType = string;
export type NotificationDeliveryStatus = string;

export interface UserActivityLog {
  id: string;
  timestamp: string;
  userId: string;
  username: string;
  role: string;
  actionPerformed: string;
  targetEntity?: string;
  details?: string;
  ipAddress?: string;
  user?: {
    roles: Role[];
  };
}
export interface SystemErrorLog {
  id: string;
  timestamp: string;
  severity: LogSeverity;
  sourceModule?: string;
  errorCode?: string;
  errorMessage: string;
  stackTrace?: string;
  requestId?: string;
}
export interface SecurityAuditEvent {
  id: string;
  timestamp: string;
  eventType: SecurityEventType;
  username?: string;
  affectedUserId?: string;
  description: string;
  ipAddress?: string;
  outcome: 'SUCCESS' | 'FAILURE' | 'BLOCKED' | 'NOT_APPLICABLE';
}
export interface NotificationDeliveryLog {
  id: string;
  timestamp: string;
  notificationType: string;
  recipient: string;
  channel: 'EMAIL' | 'SMS' | 'IN_APP';
  subjectOrSnippet: string;
  status: NotificationDeliveryStatus;
  errorMessage?: string;
  rawResponse?: unknown;
  details?: unknown;
}

export type FeedbackStatus = string;
export type FeedbackPriority = string;
export type FeedbackCategory = string;

export interface FeedbackReply {
  id: string;
  feedbackId: string;
  repliedAt: string; // ISO Date string
  repliedBy: Pick<User, 'id' | 'fullName' | 'role'>; // Simplified User
  message: string;
  isInternalNote: boolean;
}

export interface Feedback {
  id: string;
  submittedAt: string; // ISO Date string
  updatedAt: string; // ISO Date string
  subject?: string;
  comment: string;
  status: FeedbackStatus;
  priority?: FeedbackPriority | null;
  category?: FeedbackCategory | null;
  submittedBy: Pick<User, 'id' | 'fullName' | 'universityId' | 'email'>; // Simplified User
  assignedTo?: Pick<User, 'id' | 'fullName' | 'role'> | null; // Unified assignee
  studentDepartment?: string;
  studentYearLevel?: string; 
  studentSemester?: string; 
  studentContactEmail?: string;
  studentContactPhone?: string;
  attachmentUrl?: string | null; // Added for attachments
  attachmentName?: string | null; // Added for attachments
  replies: FeedbackReply[];
  isDeleted: boolean;
}

// Admin Profile & Settings Payloads
export interface AdminProfileUpdatePayload {
  fullName?: string;
  email?: string;
  phoneNumber?: string;
}

export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
}

export interface AdminNotificationPreferences {
  notifyOnNewFeedback: boolean;
  notifyOnCriticalError: boolean;
}
export type AdminNotificationPreferencesPayload = Partial<AdminNotificationPreferences>;

// API Payloads for Feedback
export interface FeedbackUpdatePayload {
  status?: FeedbackStatus;
  priority?: FeedbackPriority | null;
  category?: FeedbackCategory | null;
  assignedToId?: string | null;
}

export interface FeedbackReplyPayload {
  message: string;
  isInternalNote?: boolean;
}

export interface FeedbackUpdateByStaffPayload {
  status: 'PENDING_ADMIN_FOLLOW_UP' | 'RESOLVED';
  internalNote: string;
}

// API Response Structures
export interface ApiErrorData {
  success: boolean;
  message: string;
  errorCode: string;
  errorId: string;
  details?: Record<string, unknown>;
  stack?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface BulkUploadResult {
  successfulCreations: User[];
  failedCreations: {
    row: Record<string, unknown>; // The original row from the CSV
    error: string;
  }[];
}

export interface ValidationResult {
  errors: string[];
  warnings: string[];
  data: Record<string, string>[];
  headers: string[];
}

export interface EditableFailure {
  identifier: string; // The email or username used as key
  row: Record<string, string>;
  error: string;
  isEditing: boolean;
  isRetrying: boolean;
  retrySuccess?: boolean;
}

export type BulkUploadStage = 'upload' | 'preview' | 'processing' | 'results';

export interface BulkProgressTrackerProps {
  jobId: string | null;
  onComplete?: (log: BulkOperationLog) => void;
  title?: string;
}

export interface BulkResultsViewProps {
  log: BulkOperationLog;
  parsedCsvData: Record<string, string>[];
  onStartOver: () => void;
  onRetrySuccess?: (userData: User) => void;
}

export interface BulkOperationLog {
  id: string;
  operationType: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'PARTIAL_SUCCESS';
  totalRecords: number;
  processedRecords: number;
  successfulRecords: number;
  failedRecords: number;
  lastProcessedLine: number;
  errors?: Record<string, string[]>;
  performedById: string;
  startedAt: string;
  completedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

// Student Financial Record Types
export type StudentRegistrationStatus = string;
export type StudentOverallPaymentStatus = string;

export interface StudentFeeItem {
  id: string;
  name: string;
  amount: number;
}

export interface StudentAppliedScholarship {
  id: string;
  scholarshipName: string;
  amount: number;
  benefitType: ScholarshipBenefitType;
  academicTerm: string;
  dateApplied: string;
}

export interface StudentPenalty {
  id: string;
  reason: string;
  amount: number;
  dateIssued: string;
  status: 'OUTSTANDING' | 'PAID' | 'WAIVED';
}

export interface StudentFinancialRecordSummary {
  studentId: string;
  universityId?: string;
  fullName: string;
  department: string;
  yearLevel: string;
  email: string;
  phoneNumber?: string;
  currentAccountStatus: AccountStatus; 
  registrationStatus: StudentRegistrationStatus;
  currentTermFeeStatusText: string; 
  installmentPlanActive: boolean;
  scholarshipApplied: boolean; 
  hasActiveFullScholarship?: boolean; 
  totalPenaltiesAmount: number; 
  accountCreatedAt: string;
  lastModifiedAt: string;
}

export interface DetailedStudentFinancialRecord extends StudentFinancialRecordSummary {
  activeInstallmentPlanId?: string;
  currentTerm?: {
    // Details for the currently selected/active term
    academicTerm: string; // e.g., "2023-2024 Semester I"
    applicableFeeStructureId?: string;
    feeItems: StudentFeeItem[];
    totalFeeDue: number;
    scholarships: StudentAppliedScholarship[];
    adjustedFeeDue: number;
    totalPaidThisTerm: number;
    outstandingBalance: number;
    penalties: StudentPenalty[];
    paymentDeadline?: string;
  };
  paymentHistory: PaymentRecord[]; // Full payment history
  installmentPlans: Installment[]; // Updated to use new Installment type
  allAppliedScholarships: StudentAppliedScholarship[]; // Full history
  allPenalties: StudentPenalty[]; // Full history
  internalFinanceNotes: InternalFinanceNote[];
}

export interface InternalFinanceNote {
  id: string;
  timestamp: string;
  staffName: string; // Name of staff who added the note
  staffId: string;
  note: string;
}

// Fee Structure Management Types
export type FeeStructureStatus = string;

export interface FeeStructure {
  id: string;
  name: string;
  academicYear: string;
  semesterId: string;
  departmentId: string;
  yearLevelId: string;
  status: 'DRAFT' | 'ACTIVE' | 'ARCHIVED'; // Updated to match backend enum
  totalFee: number;
  paymentDeadlineDays?: number;
  effectiveDate: string; // ISO Date string
  expiryDate?: string; // ISO Date string
  scheduledAt?: string; // ISO Datetime string
  fixedDueDate?: string; // ISO Date string
  isApplied?: boolean;
  notes?: string;
  feeItems: FeeItem[];
  lastModifiedBy?: {
    id: string;
    fullName: string;
  };
  lastModifiedById?: string;
  lastModifiedDate: string;
  department?: Department | string; // Handled relation
  semester?: string; // Derived/Joined
  yearLevel?: string; // Derived/Joined
}

export interface FeeItem {
  feeId: string;
  amount: number;
  fee?: Fee; // Optional populated fee
}

export interface Fee {
  id: string;
  name: string;
  description?: string;
  amount: number;
  type: string; // Dynamic Enum ID
}

// Installment Plan Management Types
export type InstallmentFrequency = string;
export type InstallmentCalculationMethod = string;

export interface InstallmentPlanTemplate {
  id: string;
  name: string;
  description: string;
  numberOfInstallments: number;
  paymentFrequency: InstallmentFrequency;
  calculationMethod: InstallmentCalculationMethod;
  status: 'ACTIVE' | 'INACTIVE';
  gracePeriodDays?: number;
  latePenaltyValue?: number; // Can be amount or percent based on a related setting
  latePenaltyType?: 'FIXED' | 'PERCENTAGE';
  allowStudentSelfEnrollment: boolean;
  createdBy?: Pick<User, 'id' | 'fullName'>;
  lastModifiedBy?: Pick<User, 'id' | 'fullName'>;
}

export type AssignedPlanStatus = string;

export interface AssignedInstallmentPlan {
  assignmentId: string;
  id?: string;
  studentId: string;
  studentName: string;
  department: string;
  yearLevel: string;
  academicYear: string; // Added for better scoping
  semester: string;
  assignedPlanTemplateId: string;
  assignedPlanTemplateName: string;
  totalFeeOnPlan: number;
  numberOfInstallments: number;
  nextDueDate?: string;
  nextInstallmentAmount?: number;
  outstandingPlanBalance: number;
  status: AssignedPlanStatus;
  installments: Installment[];
}

export interface EligibleStudentForPlan {
  studentId: string;
  universityId: string;
  fullName: string;
  department: string;
  departmentId: string;
  yearLevel: string;
  applicableFee: number;
  termFeeId: string;
}

// Financial Reports Types
export type FinancialReportType = string;

// Announcement Types
export type AnnouncementStatus = string;

export interface AnnouncementTargetAudience {
  type: 'BROAD' | 'CRITERIA' | 'INDIVIDUAL';
  // This value will be constructed by the frontend.
  // For 'Broad', it's "All Students".
  // For 'Individual', it's a comma-separated list of IDs: "id1,id2,id3".
  // For 'Criteria', it's a structured JSON string: '{"departments":["dept_id_1"],"yearLevels":["Y2"]}'
  value: string;
}

export interface Announcement {
  id: string;
  title: string;
  body: string; // HTML content
  targetAudience: AnnouncementTargetAudience;
  targetAudienceDescription: string; // User-friendly description
  status: AnnouncementStatus;
  channels: ('SYSTEM' | 'EMAIL' | 'SMS')[];
  sentBy: Pick<User, 'id' | 'fullName'>;
  sentOrScheduledDate: string; // ISO date string
  attachmentUrl?: string;
  attachmentName?: string;
}

// Staff Profile Types
export interface StaffProfileUpdatePayload {
  fullName: string;
  email: string;
  phoneNumber?: string;
}

export interface StaffNotificationPreferences {
  largeTransactionAlerts: boolean;
  discrepancyAlerts: boolean;
  endOfDaySummary: boolean;
  systemMaintenanceAlerts: boolean;
  criticalGatewayAlertsSms: boolean;
}

// Student Portal & Staff Notification Types
export type NotificationPriority = 'HIGH' | 'NORMAL' | 'LOW';

export interface StudentNotification {
  id: string;
  date: string; // ISO Date string
  subject: string;
  messageSnippet: string;
  fullMessage: string;
  status: 'READ' | 'UNREAD';
  type: string; // Dynamic Enum Key
  priority?: NotificationPriority;
  actionLink?: string;
}

export interface StudentFeedbackSubmission {
  subject?: string;
  category: FeedbackCategory;
  comment: string;
  attachment?: File | null;
  departmentId: string;
  phoneNumber: string;
}

export interface Installment {
  installmentNumber: number;
  dueDate: string; // ISO Date string
  amountDue: number;
  amountPaid: number;
  status: 'PAID' | 'DUE' | 'OVERDUE' | 'PARTIALLY_PAID';
  paymentDate?: string; // ISO Date string
  penalty?: number; // Added penalty field
}

export interface StudentNotificationPreferences {
  remindersByEmail: boolean;
  remindersBySms: boolean;
}

// Toast Notification Type
export enum AlertType {
  Success = 'success',
  Error = 'error',
  Warning = 'warning',
  Info = 'info',
  Institutional = 'institutional',
}
export interface Toast {
  id: string;
  type: AlertType;
  message: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  duration?: number;
}

// System Alert Type (for persistent on-page alerts)
export interface SystemAlert {
  id: string;
  type: AlertType;
  message: string;
  details?: string;
  timestamp: Date;
}

// --- NEW DYNAMIC TYPES FOR PHASE 4 ---

export interface PaymentGateway {
  id: string;
  name: string;
  gatewayKey: string;
  processorModule: string;
  isEnabled: boolean;
  config: {
    apiKey?: string;
    secretKey?: string;
    webhookSecret?: string;
    logoUrl?: string;
    [key: string]: unknown; // for other potential config values
  };
  isDeletable?: boolean;
}

export interface FeatureFlag {
  id: string;
  key: string;
  description?: string;
  isEnabled: boolean;
}

// Role-related types moved to types/role.types.ts

// Permissions, RoleWithDetails, RoleUsersPageParams, RoleBreakdownProps, RoleInfo moved to types/role.types.ts

export interface WorkflowState {
  id: string;
  workflowId: string;
  name: string;
  description?: string;
  isInitial: boolean;
  isFinal: boolean;
  statusId?: string;
  status?: { key: string; displayName: string };
  metadata?: unknown; // JSON type in Prisma
}

export interface Workflow {
  id: string;
  name: string;
  description?: string;
  entityType: string;
  entityStatusField: string;
  statusId: string; // Foreign key to DynamicEnum
  status?: { key: string; displayName: string; colorHint?: string }; // The actual DynamicEnum object
  visibilityId: string; // Foreign key to DynamicEnum
  visibility?: { key: string; displayName: string; colorHint?: string }; // The actual DynamicEnum object
  metadata?: unknown; // JSON type in Prisma
  states?: WorkflowState[]; // Optional, as it's a relation
  createdAt: string; // DateTime in Prisma maps to string (ISO date)
  updatedAt: string; // DateTime in Prisma maps to string (ISO date)
}

export interface CreateWorkflowPayload {
  name: string;
  description?: string;
  entityType: string;
  entityStatusField: string;
  statusId?: string; // Optional (backend defaults to DRAFT)
  visibilityId?: string; // Optional (backend defaults to INTERNAL)
  metadata?: Record<string, unknown>;
}

export interface UpdateWorkflowPayload {
  name?: string;
  description?: string;
  entityType?: string;
  entityStatusField?: string;
  statusId?: string;
  visibilityId?: string;
}

// --- RECONCILIATION TYPES ---

export interface ReconciliationReport {
  id: string;
  gateway: string;
  reportDate: string; // ISO Date
  status: string;
  summary: unknown;
  discrepancies?: ReconciliationDiscrepancy[];
  createdAt: string;
}

export interface ReconciliationDiscrepancy {
  id: string;
  reportId: string;
  paymentRecordId?: string;
  gatewayRef: string;
  type: string;
  details: unknown;
  status: string;
  resolvedAt?: string;
}

// --- COST MANAGEMENT TYPES ---

export interface Budget {
  id: string;
  name: string;
  departmentId?: string;
  period: string;
  allocatedAmount: number;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface Expense {
  id: string;
  description: string;
  amount: number;
  date: string; // ISO Date
  vendor?: string;
  budgetId: string;
  recordedById: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBudgetPayload {
  name: string;
  departmentId?: string;
  period: string;
  allocatedAmount: number;
  status: string;
}

export interface UpdateBudgetPayload {
  name?: string;
  departmentId?: string;
  period?: string;
  allocatedAmount?: number;
  status?: string;
}

export interface CreateExpensePayload {
  description: string;
  amount: number;
  date: string; // ISO Date or Date object
  vendor?: string;
  budgetId: string;
}

export interface UpdateExpensePayload {
  description?: string;
  amount?: number;
  date?: string; // ISO Date or Date object
  vendor?: string;
  budgetId?: string;
}

// --- GOVERNANCE TYPES ---

// Governance types consolidated in governance.types.ts

// Observability & Monitoring Types

/**
 * Represents a single value entry from Prometheus metrics.
 * Used for parsing /metrics endpoint responses.
 */
export interface PrometheusMetricValue {
  /** Labels for this metric value (e.g., method, status_code, le for histograms) */
  labels: Record<string, string>;
  /** The numeric value of the metric */
  value: number | string;
}

/**
 * Represents a Prometheus metric with its name and values.
 * Matches the backend's Prometheus metrics format.
 */
export interface PrometheusMetric {
  /** Metric name (e.g., http_requests_total, http_request_duration_seconds) */
  name: string;
  /** Help text describing the metric */
  help?: string;
  /** Type of metric (counter, gauge, histogram, summary) */
  type?: 'counter' | 'gauge' | 'histogram' | 'summary';
  /** Array of metric values with their labels */
  values?: PrometheusMetricValue[];
}

// Lookup Types (for dynamic enum dropdowns)

/**
 * Generic lookup type used for dynamic dropdown options.
 * Matches the backend DynamicEnumService structure.
 */
export interface Lookup {
  id: string;
  /** Name field (for database models like Department) */
  name?: string;
  /** Value field (for enum-like lookups) */
  value?: string;
  /** Display label (for enum-like lookups) */
  label?: string;
  /** Optional color for status badges */
  color?: string;
  /** Display Name (often used in UI) */
  displayName?: string;
  /** Key (often used for logic/enums) */
  key?: string;
  /** System protected flag */
  isSystem?: boolean;
  isActive?: boolean;
  /** Allow additional dynamic properties */
  [key: string]: unknown;
}

/**
 * Normalized lookup option with required value and label.
 * Use this when you need guaranteed non-nullable fields.
 */
export interface NormalizedLookupOption {
  value: string;
  label: string;
}

/**
 * Type alias for NormalizedLookupOption for easier usage in UI forms.
 */
export type LookupOption = NormalizedLookupOption;

// --- ACADEMIC PROMOTION TYPES ---

export type {
  AcademicSession,
  PromotionLog,
  AcademicRecord,
  ImportChunk,
  ImportSession,
  PromotionReportFilters,
  ImportStep,
  ValidationError,
  PaginatedResults,
  CursorPaginatedResults,
  PreviewDepartmentStat,
  PreviewTotals,
  PreviewData,
  GateInputType,
  GateDefinition,
  GlobalDefaults,
  DepartmentOverride,
  GateTimeouts,
  PromotionConfig,
  GateConfigMetadata,
  GateSubField,
  GateFailureDetail,
  ExceptionStudentDetail,
  PromotionExceptionResult,
  UsePromotionExceptionsFilters,
  PromotionExceptionsInspectorProps,
  BreakGlassLog,
  EligibleChecker,
  ActivateBreakGlassPayload,
  ActivateBreakGlassResult,
  GetBreakGlassStateResult,
} from './types/domain/academic.types';




